import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useApiGet } from '../utils/httpClientTs';
import { getGroupTypes, getGroupRolesByType } from '../endpoints/groups';
import { GroupPermission, GroupRole, GroupType } from '../types/group';
import AlertItem from './AlertItem';

const SCOPES = [
  { value: 'international', label: 'International' },
  { value: 'spd_chapter', label: 'SPD Chapter' },
  { value: 'local_chapter', label: 'Local Chapter' },
] as const;

const ENABLE_ROLE_SELECTION = true;

export interface TrainingGroupPermissionsEditorProps {
  /** Selected group-type access rules (controlled). */
  value: Map<string, GroupPermission>;
  onChange: (next: Map<string, GroupPermission>) => void;
}

const TrainingGroupPermissionsEditor: React.FC<
  TrainingGroupPermissionsEditorProps
> = ({ value, onChange }) => {
  const [groupTypeSearches, setGroupTypeSearches] = useState<
    Record<string, string>
  >({
    international: '',
    spd_chapter: '',
    local_chapter: '',
  });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupScopeMap, setGroupScopeMap] = useState<Map<string, string>>(
    new Map()
  );

  const {
    data: internationalGroups = [],
    isLoading: loadingInternational,
    error: internationalError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'international', 'training-editor'],
    () => getGroupTypes('international'),
    { enabled: true }
  );
  const {
    data: spdGroups = [],
    isLoading: loadingSpd,
    error: spdError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'spd_chapter', 'training-editor'],
    () => getGroupTypes('spd_chapter'),
    { enabled: true }
  );
  const {
    data: localGroups = [],
    isLoading: loadingLocal,
    error: localError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'local_chapter', 'training-editor'],
    () => getGroupTypes('local_chapter'),
    { enabled: true }
  );

  useEffect(() => {
    const allGroups = [
      ...internationalGroups.map((gt) => ({ ...gt, scope: 'international' })),
      ...spdGroups.map((gt) => ({ ...gt, scope: 'spd_chapter' })),
      ...localGroups.map((gt) => ({ ...gt, scope: 'local_chapter' })),
    ];
    setGroupScopeMap((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const id of value.keys()) {
        if (next.has(id)) continue;
        const g = allGroups.find((gt) => gt.id === id);
        if (g) {
          next.set(id, g.scope);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [value, internationalGroups, spdGroups, localGroups]);

  const filteredGroupsByScope = useMemo(() => {
    const selectedIds = new Set(value.keys());
    return {
      international: internationalGroups.filter(
        (gt) =>
          !selectedIds.has(gt.id) &&
          gt.name
            .toLowerCase()
            .includes(groupTypeSearches.international.toLowerCase())
      ),
      spd_chapter: spdGroups.filter(
        (gt) =>
          !selectedIds.has(gt.id) &&
          gt.name
            .toLowerCase()
            .includes(groupTypeSearches.spd_chapter.toLowerCase())
      ),
      local_chapter: localGroups.filter(
        (gt) =>
          !selectedIds.has(gt.id) &&
          gt.name
            .toLowerCase()
            .includes(groupTypeSearches.local_chapter.toLowerCase())
      ),
    };
  }, [internationalGroups, spdGroups, localGroups, value, groupTypeSearches]);

  const handleGroupTypeSelect = (
    groupType: GroupType | null,
    scope: string
  ) => {
    if (!groupType) return;
    const newPermission: GroupPermission = {
      group_type_id: groupType.id,
      all_roles: !ENABLE_ROLE_SELECTION,
      role_ids: ENABLE_ROLE_SELECTION ? [] : undefined,
    };
    const updated = new Map(value);
    updated.set(groupType.id, newPermission);
    onChange(updated);
    setGroupScopeMap((prev) => new Map(prev).set(groupType.id, scope));
    setGroupTypeSearches((prev) => ({ ...prev, [scope]: '' }));
    if (ENABLE_ROLE_SELECTION) {
      setExpandedGroups((prev) => new Set(prev).add(groupType.id));
    }
  };

  const handleRemoveGroup = (groupTypeId: string) => {
    const updated = new Map(value);
    updated.delete(groupTypeId);
    onChange(updated);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.delete(groupTypeId);
      return next;
    });
  };

  const handleToggleExpand = (groupTypeId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupTypeId)) next.delete(groupTypeId);
      else next.add(groupTypeId);
      return next;
    });
  };

  const handleSelectAllRoles = (groupTypeId: string, checked: boolean) => {
    const current = value.get(groupTypeId);
    if (!current) return;
    const updated = new Map(value);
    updated.set(groupTypeId, {
      ...current,
      all_roles: checked,
      role_ids: checked ? undefined : [],
    });
    onChange(updated);
  };

  const handleRoleToggle = (groupTypeId: string, roleId: string) => {
    const current = value.get(groupTypeId);
    if (!current) return;
    const currentRoleIds = current.role_ids || [];
    const newRoleIds = currentRoleIds.includes(roleId)
      ? currentRoleIds.filter((id) => id !== roleId)
      : [...currentRoleIds, roleId];
    const updated = new Map(value);
    updated.set(groupTypeId, {
      ...current,
      all_roles: false,
      role_ids: newRoleIds,
    });
    onChange(updated);
  };

  const getGroupTypeName = (groupTypeId: string): string => {
    const scope = groupScopeMap.get(groupTypeId);
    const allGroups = [...internationalGroups, ...spdGroups, ...localGroups];
    if (!scope) {
      return allGroups.find((gt) => gt.id === groupTypeId)?.name || groupTypeId;
    }
    const scopeGroups =
      scope === 'international'
        ? internationalGroups
        : scope === 'spd_chapter'
          ? spdGroups
          : localGroups;
    return scopeGroups.find((gt) => gt.id === groupTypeId)?.name || groupTypeId;
  };

  const getGroupScopeLabel = (groupTypeId: string): string => {
    const scope = groupScopeMap.get(groupTypeId);
    if (!scope) return '';
    return SCOPES.find((s) => s.value === scope)?.label || scope;
  };

  const selectedEntries = useMemo(() => {
    const all = [...internationalGroups, ...spdGroups, ...localGroups];
    const nameOf = (id: string) => all.find((g) => g.id === id)?.name || id;
    return Array.from(value.entries()).sort(([a], [b]) =>
      nameOf(a).localeCompare(nameOf(b))
    );
  }, [value, internationalGroups, spdGroups, localGroups]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, maxHeight: 480, overflow: 'auto' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Add group types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            International, then SPD, then local chapter — same ordering as other
            permission screens.
          </Typography>
          {SCOPES.map((scope) => {
            const groups = filteredGroupsByScope[scope.value];
            const isLoading =
              scope.value === 'international'
                ? loadingInternational
                : scope.value === 'spd_chapter'
                  ? loadingSpd
                  : loadingLocal;
            const error =
              scope.value === 'international'
                ? internationalError
                : scope.value === 'spd_chapter'
                  ? spdError
                  : localError;
            return (
              <Box key={scope.value} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {scope.label}
                </Typography>
                <Autocomplete
                  options={groups}
                  getOptionLabel={(option) => option.name}
                  value={null}
                  inputValue={groupTypeSearches[scope.value]}
                  onInputChange={(_, newValue) =>
                    setGroupTypeSearches((prev) => ({
                      ...prev,
                      [scope.value]: newValue,
                    }))
                  }
                  onChange={(_, newValue) =>
                    handleGroupTypeSelect(newValue, scope.value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={`Search ${scope.label.toLowerCase()}…`}
                      size="small"
                      fullWidth
                    />
                  )}
                  loading={isLoading}
                  disabled={isLoading}
                />
                {error && (
                  <AlertItem
                    severity="error"
                    description={`Failed to load ${scope.label.toLowerCase()} groups.`}
                    onClose={() => {}}
                    keepOpen={true}
                  />
                )}
                {isLoading ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 1 }}
                  >
                    <CircularProgress size={22} />
                  </Box>
                ) : groups.length > 0 ? (
                  <List dense disablePadding sx={{ mt: 0.5 }}>
                    {groups.slice(0, 12).map((groupType) => (
                      <ListItem
                        key={groupType.id}
                        disablePadding
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() =>
                              handleGroupTypeSelect(groupType, scope.value)
                            }
                            sx={{ color: '#0072CE' }}
                          >
                            <AddIcon />
                          </IconButton>
                        }
                      >
                        <ListItemButton
                          onClick={() =>
                            handleGroupTypeSelect(groupType, scope.value)
                          }
                        >
                          <ListItemText primary={groupType.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No groups match search.
                  </Typography>
                )}
                {scope.value !== SCOPES[SCOPES.length - 1].value && (
                  <Divider sx={{ mt: 1 }} />
                )}
              </Box>
            );
          })}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, maxHeight: 480, overflow: 'auto' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Selected ({value.size})
          </Typography>
          {value.size > 0 ? (
            <List disablePadding>
              {selectedEntries.map(([groupTypeId, permission]) => (
                <TrainingGroupPermissionItem
                  key={groupTypeId}
                  groupTypeId={groupTypeId}
                  groupTypeName={getGroupTypeName(groupTypeId)}
                  permission={permission}
                  expanded={expandedGroups.has(groupTypeId)}
                  enableRoleSelection={ENABLE_ROLE_SELECTION}
                  onToggleExpand={handleToggleExpand}
                  onSelectAllRoles={handleSelectAllRoles}
                  onRoleToggle={handleRoleToggle}
                  onRemove={handleRemoveGroup}
                  getGroupScopeLabel={getGroupScopeLabel}
                />
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Leave empty to allow <strong>all</strong> signed-in users to view
              this training. Add one or more group types to restrict access.
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

interface TrainingGroupPermissionItemProps {
  groupTypeId: string;
  groupTypeName: string;
  permission: GroupPermission;
  expanded: boolean;
  enableRoleSelection: boolean;
  onToggleExpand: (groupTypeId: string) => void;
  onSelectAllRoles: (groupTypeId: string, checked: boolean) => void;
  onRoleToggle: (groupTypeId: string, roleId: string) => void;
  onRemove: (groupTypeId: string) => void;
  getGroupScopeLabel: (id: string) => string;
}

const TrainingGroupPermissionItem: React.FC<
  TrainingGroupPermissionItemProps
> = ({
  groupTypeId,
  groupTypeName,
  permission,
  expanded,
  enableRoleSelection,
  onToggleExpand,
  onSelectAllRoles,
  onRoleToggle,
  onRemove,
  getGroupScopeLabel,
}) => {
  const {
    data: roles = [],
    isLoading: loadingRoles,
    error: rolesError,
  } = useApiGet<GroupRole[]>(
    ['group-roles', groupTypeId, 'training-editor'],
    () => getGroupRolesByType(groupTypeId),
    { enabled: expanded && enableRoleSelection }
  );

  const sortedRoles = useMemo(() => {
    return [...roles].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [roles]);

  return (
    <ListItem
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        flexDirection: 'column',
        alignItems: 'stretch',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            flexWrap: 'wrap',
          }}
        >
          <Chip
            label={groupTypeName}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Chip
            label={getGroupScopeLabel(groupTypeId)}
            color="secondary"
            variant="outlined"
            size="small"
          />
          {permission.all_roles ? (
            <Chip label="All Roles" color="success" size="small" />
          ) : (
            permission.role_ids &&
            permission.role_ids.length > 0 && (
              <Chip
                label={`${permission.role_ids.length} role${
                  permission.role_ids.length > 1 ? 's' : ''
                }`}
                size="small"
              />
            )
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {enableRoleSelection && (
            <IconButton
              size="small"
              onClick={() => onToggleExpand(groupTypeId)}
              sx={{ color: '#0072CE' }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => onRemove(groupTypeId)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
      {expanded && enableRoleSelection && (
        <Box sx={{ mt: 2, width: '100%', pl: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={permission.all_roles}
                onChange={(e) =>
                  onSelectAllRoles(groupTypeId, e.target.checked)
                }
                size="small"
              />
            }
            label="Select All Roles"
            sx={{ mb: 0.5 }}
          />
          {loadingRoles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          ) : rolesError ? (
            <AlertItem
              severity="error"
              description="Failed to load roles."
              onClose={() => {}}
              keepOpen={true}
            />
          ) : (
            sortedRoles.map((role) => (
              <FormControlLabel
                key={role.id}
                control={
                  <Checkbox
                    checked={
                      permission.all_roles ||
                      (permission.role_ids || []).includes(role.id)
                    }
                    onChange={() => onRoleToggle(groupTypeId, role.id)}
                    disabled={permission.all_roles}
                    size="small"
                  />
                }
                label={role.name}
                sx={{ display: 'block', mb: 0.25 }}
              />
            ))
          )}
        </Box>
      )}
    </ListItem>
  );
};

export default TrainingGroupPermissionsEditor;
